import {
  getReadonlyListItemAsset,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
  getReadonlyListItemYesOrNo,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import type {
  IxcAssetsV1AssetState,
  IxcRaglV1Delta,
  IxcRaglV1PortfolioLink,
  IxcRaglV1ReversedDetails,
  IxcRaglV1ReverseLink,
} from '@interxfi/types/generated/api';
import { getPortfolioTransactionDetailPath } from '../portfolio/transaction/portfolio-transaction-routing';
import { getRegistrationAccountDetailPath } from '../registration/account/registration-account-routing';
import { getRaglTransactionDetailPath } from './transaction/ragl-transaction-routing';

export function getRaglReversedDetailListItems(data: IxcRaglV1ReversedDetails | undefined, prefix = '') {
  return {
    reversedPostedTimestamp: getReadonlyListItemDate(
      `${prefix}reversedPostedTimestamp`,
      'Posted Timestamp',
      data?.reversePostedTimestamp,
      'timestamp_iso_s',
    ),
    effectiveAt: getReadonlyListItemDate(`${prefix}effectiveAt`, 'Effective At', data?.effectiveAt, 'timestamp_iso_s'),
    transactionId: getReadonlyListItemId(
      `${prefix}transactionId`,
      'Transaction ID',
      data?.transactionId,
      getRaglTransactionDetailPath(data?.transactionId),
    ),
    reason: data?.reason ? getReadonlyListItemString(`${prefix}reason`, 'Reason', data.reason) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRaglDeltaListItems(data: IxcRaglV1Delta | undefined, asset: IxcAssetsV1AssetState | undefined, prefix = '') {
  return {
    seq: getReadonlyListItemNumber(`${prefix}seq`, 'Sequence', data?.seq),
    accountCode: getReadonlyListItemString(`${prefix}accountCode`, 'Account Code', data?.accountCode),
    brokerageAccountId: data?.brokerageAccountId
      ? getReadonlyListItemId(
          `${prefix}brokerageAccountId`,
          'Brokerage Account ID',
          data.brokerageAccountId,
          getRegistrationAccountDetailPath(data.brokerageAccountId),
        )
      : undefined,
    amount: getReadonlyListItemAsset(`${prefix}amount`, 'Amount', data?.amount, asset?.data),
    isReverse: getReadonlyListItemYesOrNo(`${prefix}isReverse`, 'Is Reverse', Boolean(data?.isReverse)),
  } as const satisfies ReadonlyListItemRecord;
}

export function getRaglReverseLinkListItems(data: IxcRaglV1ReverseLink | undefined, prefix = '') {
  return {
    transactionId: data?.transactionId
      ? getReadonlyListItemId(`${prefix}transactionId`, 'Transaction ID', data.transactionId, getRaglTransactionDetailPath(data.transactionId))
      : undefined,
    reason: data?.reason ? getReadonlyListItemString(`${prefix}reason`, 'Reason', data.reason) : undefined,
  } as const satisfies ReadonlyListItemRecord;
}

export function getRaglPortfolioLinkListItems(data: IxcRaglV1PortfolioLink | undefined, prefix = '') {
  return {
    accountId: getReadonlyListItemId(`${prefix}accountId`, 'Account ID', data?.accountId, getRegistrationAccountDetailPath(data?.accountId)),
    transactionId: getReadonlyListItemId(
      `${prefix}transactionId`,
      'Transaction ID',
      data?.transactionId,
      getPortfolioTransactionDetailPath(data?.accountId, data?.transactionId),
    ),
    event: getReadonlyListItemTranslatedString(`${prefix}event`, 'Event', {
      namespace: 'ixcRaglV1',
      enumName: 'IxcRaglV1PortfolioLinkPortfolioEvent',
      value: data?.event,
    }),
  } as const satisfies ReadonlyListItemRecord;
}
